
import { Button } from "@/components/ui/button";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { Check } from "lucide-react";
import { SelectedExercise } from "../ExerciseSelectionDialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from "@/hooks/use-mobile";

interface ExerciseCardProps {
  exercise: SelectedExercise;
  index: number;
  selectedCount: number;
  multiSelectMode: boolean;
  onSelectExercise: (exercise: SelectedExercise) => void;
  isSelected: boolean;
}

export function ExerciseCard({
  exercise,
  index,
  selectedCount,
  multiSelectMode,
  onSelectExercise,
  isSelected
}: ExerciseCardProps) {
  const isMobile = useIsMobile();
  
  return (
    <div className={`bg-white border rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all 
      dark:bg-gray-800 dark:border-gray-700 
      ${isSelected ? 'border-primary border-2 dark:border-primary' : 'border-slate-200 dark:border-gray-700'}`}
      onClick={() => onSelectExercise(exercise)}
    >
      <div className="relative overflow-hidden group">
        {isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white font-bold z-10 shadow-sm">
            {index + 1}
          </div>
        )}
        
        <AspectRatio ratio={1/1} className="bg-slate-50 dark:bg-gray-700">
          <ExerciseImage
            imagePath={exercise.isCustom ? exercise.image_url : (exercise.gif_url || "")}
            altText={exercise.name}
            fallbackText={exercise.name}
            className="w-full h-full object-contain p-2"
            isCustomExercise={exercise.isCustom}
          />
        </AspectRatio>
        
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant={isSelected ? "destructive" : "secondary"} size="sm" className="gap-1 rounded-full shadow-md">
            <Check className="h-4 w-4" />
            {isSelected ? 'Desmarcar' : 'Selecionar'}
          </Button>
        </div>
      </div>
      
      <div className="p-3 space-y-2">
        <h3 className="text-xs sm:text-sm font-medium line-clamp-2 dark:text-white">{exercise.name}</h3>
        <div className="flex flex-wrap gap-1.5">
          {exercise.body_part && (
            <span className="inline-flex text-xs px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full dark:bg-blue-900/50 dark:text-blue-200">
              {exercise.body_part}
            </span>
          )}
          {exercise.target_muscle && (
            <span className="inline-flex text-xs px-2.5 py-0.5 bg-green-50 text-green-700 rounded-full dark:bg-green-900/50 dark:text-green-200">
              {exercise.target_muscle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
